import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Eye, MessageSquareText, RotateCcw, ShieldCheck, TimerReset, UserRound, X } from "lucide-react";
import { api, type AlertDetail, type Incident, type IncidentDetail, type User } from "../api/client";

type IncidentAction = "acknowledge" | "resolve" | "close" | "reopen";

export function IncidentsPage({ initialIncidentId = "", canAssign = false }: { initialIncidentId?: string; canAssign?: boolean }) {
  const queryClient = useQueryClient();
  const [selectedIncidentId, setSelectedIncidentId] = useState(initialIncidentId);
  const [note, setNote] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const incidents = useQuery({ queryKey: ["incidents"], queryFn: () => api<Incident[]>("/api/admin/incidents") });
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<User[]>("/api/admin/users"), enabled: canAssign });
  const detail = useQuery({
    queryKey: ["incident-detail", selectedIncidentId],
    queryFn: () => api<IncidentDetail>(`/api/admin/incidents/${selectedIncidentId}`),
    enabled: Boolean(selectedIncidentId)
  });

  const openCount = useMemo(() => (
    (incidents.data ?? []).filter((incident) => ["open", "acknowledged", "assigned", "reopened"].includes(incident.status)).length
  ), [incidents.data]);
  const breachedCount = useMemo(() => (
    (incidents.data ?? []).filter((incident) => incident.slaBreached).length
  ), [incidents.data]);

  const actionMutation = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: IncidentAction; note?: string }) => api<Incident>(`/api/admin/incidents/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ note })
    }),
    onSuccess: async () => {
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["incidents"] });
      await queryClient.invalidateQueries({ queryKey: ["incident-detail", selectedIncidentId] });
    }
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, assignedToUserId, note }: { id: string; assignedToUserId: string | null; note?: string }) => api<Incident>(`/api/admin/incidents/${id}/assign`, {
      method: "POST",
      body: JSON.stringify({ assignedToUserId, note })
    }),
    onSuccess: async () => {
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["incidents"] });
      await queryClient.invalidateQueries({ queryKey: ["incident-detail", selectedIncidentId] });
    }
  });

  const commentMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api(`/api/admin/incidents/${id}/comments`, {
      method: "POST",
      body: JSON.stringify({ note })
    }),
    onSuccess: async () => {
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["incident-detail", selectedIncidentId] });
    }
  });

  function submitComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedIncidentId || !note.trim()) return;
    commentMutation.mutate({ id: selectedIncidentId, note: note.trim() });
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card alerts-hero">
        <div>
          <span className="panel-eyebrow left">Incident Ops</span>
          <h2>Red flag ocjene se vode kao SLA incidenti.</h2>
          <p>Acknowledge, assign, resolve, close i reopen akcije ostaju u audit logu, uz timeline za operativni tim.</p>
        </div>
        <div className="object-brand-card">
          <ShieldCheck size={34} />
          <strong>{openCount} otvoreno</strong>
          <span>{breachedCount} SLA breach</span>
        </div>
      </section>

      <div className="grid-two narrow">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Incidenti</h2>
              <p>Lista je scope-ovana po objektima i lokacijama trenutnog naloga.</p>
            </div>
          </div>
          <div className="incident-list">
            {(incidents.data ?? []).map((incident) => (
              <article className="incident" key={incident.id}>
                <ShieldCheck size={18} />
                <div>
                  <strong>{incident.incidentNumber} - {incident.title}</strong>
                  <span>{incident.hotel.name} / {incident.location.name} / {incident.submission.unitCode ?? "bez unit-a"}</span>
                </div>
                <button type="button" onClick={() => setSelectedIncidentId(incident.id)}><Eye size={16} /> Detalj</button>
                <code>{incident.status}{incident.slaBreached ? " / SLA breach" : ""}</code>
              </article>
            ))}
            {incidents.isLoading && <p>Ucitavanje incidenta...</p>}
            {!incidents.isLoading && !(incidents.data ?? []).length && <p>Nema incidenta za trenutni scope.</p>}
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Detalj</h2>
              <p>Timeline, guest context i recovery akcije.</p>
            </div>
            {selectedIncidentId && <button type="button" onClick={() => setSelectedIncidentId("")}><X size={16} /> Zatvori</button>}
          </div>
          {!selectedIncidentId && <p>Izaberi incident iz liste.</p>}
          {detail.isLoading && <p>Ucitavanje detalja...</p>}
          {detail.isError && <p className="form-error">Incident nije dostupan za trenutni scope.</p>}
          {detail.data && (
            <IncidentDetailPanel
              detail={detail.data}
              users={users.data ?? []}
              note={note}
              assigneeId={assigneeId}
              onNoteChange={setNote}
              onAssigneeChange={setAssigneeId}
              onAction={(action) => actionMutation.mutate({ id: detail.data.id, action, note: note.trim() || undefined })}
              onAssign={() => assignMutation.mutate({ id: detail.data.id, assignedToUserId: assigneeId || null, note: note.trim() || undefined })}
              onComment={submitComment}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function IncidentDetailPanel({
  detail,
  users,
  note,
  assigneeId,
  onNoteChange,
  onAssigneeChange,
  onAction,
  onAssign,
  onComment
}: {
  detail: IncidentDetail;
  users: User[];
  note: string;
  assigneeId: string;
  onNoteChange: (value: string) => void;
  onAssigneeChange: (value: string) => void;
  onAction: (action: IncidentAction) => void;
  onAssign: () => void;
  onComment: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const followUp = detail.submission.lowScoreFollowUp;
  return (
    <div className="page-stack">
      <section className="incident-detail-block">
        <h3>{detail.incidentNumber}</h3>
        <div className="rule-meta">
          <span>{detail.severity}</span>
          <span>{detail.status}</span>
          <span>{detail.slaBreached ? "SLA breach" : "SLA ok"}</span>
          <span>{detail.submission.language}</span>
        </div>
        <p><strong>{detail.hotel.name}</strong> - {detail.location.name}{detail.submission.subLocation ? ` - ${detail.submission.subLocation.name}` : ""}</p>
        <p>{detail.summary}</p>
        <p>QR: <code>{detail.submission.requestPath ?? "-"}</code></p>
        <p>Unit: {detail.submission.unitCode ?? "-"}</p>
      </section>

      <section className="incident-detail-block">
        <h3>PII pristup</h3>
        <p>Contact permission: {detail.submission.contactPermission ? "Da" : "Ne"}</p>
        <p>Soba: {piiValue(detail.submission.roomNumber, detail.submission.roomRedacted, "pii.view_room")}</p>
        <p>Ime i prezime: {piiValue(detail.submission.contactName, detail.submission.contactRedacted, "pii.view_contact")}</p>
        <p>Email: {piiValue(detail.submission.contactEmail, detail.submission.contactRedacted, "pii.view_contact")}</p>
        <p>Telefon: {piiValue(detail.submission.contactPhone, detail.submission.contactRedacted, "pii.view_contact")}</p>
      </section>

      <section className="incident-detail-block">
        <h3>Recovery akcije</h3>
        <div className="row-actions">
          <button type="button" onClick={() => onAction("acknowledge")}><TimerReset size={16} /> Ack</button>
          <button type="button" onClick={() => onAction("resolve")}><CheckCircle2 size={16} /> Resolve</button>
          <button type="button" onClick={() => onAction("close")}><ShieldCheck size={16} /> Close</button>
          <button type="button" onClick={() => onAction("reopen")}><RotateCcw size={16} /> Reopen</button>
        </div>
        <div className="inline-form-grid">
          <label>
            Assignee
            <select value={assigneeId} onChange={(event) => onAssigneeChange(event.target.value)}>
              <option value="">Bez assignee-a</option>
              {users.map((user) => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}
            </select>
          </label>
          <label>
            Napomena
            <input value={note} onChange={(event) => onNoteChange(event.target.value)} placeholder="Sta je uradjeno ili kome ide?" />
          </label>
        </div>
        <div className="row-actions">
          <button type="button" onClick={onAssign}><UserRound size={16} /> Assign</button>
          <form onSubmit={onComment} className="row-actions">
            <button type="submit"><MessageSquareText size={16} /> Dodaj komentar</button>
          </form>
        </div>
      </section>

      <section className="incident-detail-block">
        <h3>Low-score follow-up</h3>
        {followUp ? (
          <>
            <p>Kategorije: {followUp.triggeredCategories.join(", ") || "-"}</p>
            <p>Razlozi: {followUp.selectedReasons.join(", ") || "-"}</p>
            <p>Kontakt dozvola: {followUp.contactPermission ? "Da" : "Ne"}</p>
            {followUp.commentRedacted ? <p className="status">Komentar je redacted za ovaj nalog.</p> : <p>{followUp.comment || "Komentar nije unesen."}</p>}
          </>
        ) : (
          <p>Nema follow-up odgovora.</p>
        )}
      </section>

      <section className="incident-detail-block">
        <h3>Timeline</h3>
        <div className="recipient-list">
          {detail.events.map((event) => (
            <article className="recipient-card" key={event.id}>
              <div>
                <strong>{event.type}</strong>
                <span>{event.note ?? "Bez napomene"}</span>
                <small>{new Date(event.createdAt).toLocaleString()}</small>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="incident-detail-block">
        <h3>Odgovori</h3>
        <div className="table-shell">
          <table>
            <thead>
              <tr>
                <th>Pitanje</th>
                <th>Tip</th>
                <th>Vrijednost</th>
              </tr>
            </thead>
            <tbody>
              {detail.submission.answers.map((answer, index) => (
                <tr key={`${answer.questionKey}-${answer.answerType}-${index}`}>
                  <td>{answer.questionKey}</td>
                  <td>{answer.answerType}</td>
                  <td>{answerValue(answer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function answerValue(answer: AlertDetail["answers"][number]) {
  if (answer.numericValue !== null && answer.numericValue !== undefined) return answer.numericValue.toString();
  if (answer.booleanValue !== null && answer.booleanValue !== undefined) return answer.booleanValue ? "Da" : "Ne";
  if (answer.optionValue) return answer.optionValue;
  if (answer.textRedacted) return "[redacted]";
  if (answer.textValue) return answer.textValue;
  return "-";
}

function piiValue(value: string | null | undefined, redacted: boolean | undefined, permission: string) {
  if (value) return value;
  if (redacted) return `Sakriveno: nalog nema ${permission}.`;
  return "-";
}
